import type { ScenarioDisplay, ScenarioSourceItem } from "@/lib/powtoon-be/types";
import type { SourceInputDraft } from "@/lib/scenarios/sourceDraft";
import type { DisplayFormValue } from "@/components/scenarios/LearnerDisplayPicker";

/**
 * Resolve the editor's `DisplayFormValue` (which references uploads by
 * `draftId`) into a persistable `ScenarioDisplay`. Returns `undefined`
 * when there's no display config at all.
 *
 * `finalInputs` may be omitted at form time (before uploads resolve);
 * in that case pending uploads are dropped and the caller (save
 * pipeline) rewrites them post-upload.
 */
export function resolveDisplayForPersist(
  display: DisplayFormValue | null | undefined,
  drafts: SourceInputDraft[],
  finalInputs?: ScenarioSourceItem[],
): ScenarioDisplay | undefined {
  if (!display) return undefined;
  const base = { layout: display.layout, avatar_position: display.avatarPosition };
  if (!display.selection) return base;
  if (display.selection.kind === "url") {
    const url = display.selection.url.trim();
    // If we have a resolved input list, verify the URL is still present.
    if (finalInputs && !finalInputs.some((i) => i.kind === "url" && i.url === url)) {
      return base;
    }
    if (!url) return base;
    return { ...base, source: { kind: "url", url } };
  }
  const draftId = display.selection.draftId;
  const draft = drafts.find(
    (d): d is Extract<SourceInputDraft, { kind: "upload" }> =>
      d.kind === "upload" && d._draftId === draftId,
  );
  if (!draft?.object_id) return base;
  if (
    finalInputs &&
    !finalInputs.some((i) => i.kind === "upload" && i.object_id === draft.object_id)
  ) {
    return base;
  }
  return {
    ...base,
    source: {
      kind: "upload",
      object_id: draft.object_id,
      file_name: draft.file_name,
      mime: draft.mime,
    },
  };
}
/**
 * Only PDFs are pageable: our viewer renders them itself, so the avatar can
 * turn pages and read them. Everything else is an opaque embed.
 *
 * Shared by the save pipeline and the session runtime — both have to agree on
 * whether the paging tools exist for a scenario.
 */
export function isPdfDisplaySource(
  source?: import("@/lib/powtoon-be/types").ScenarioDisplaySource | null,
): boolean {
  if (!source) return false;
  if (source.kind === "upload") {
    return (
      (source.mime || "").toLowerCase() === "application/pdf" ||
      /\.pdf$/i.test(source.file_name || "")
    );
  }
  return /\.pdf(\?|#|$)/i.test(source.url || "");
}
